/**
 * annoMaterialize — ANNOAUTOSCALE materialization helpers (PR-6).
 *
 * When the user changes CANNOSCALE while ANNOAUTOSCALE > 0 (the AutoCAD
 * default of 4), every annotative-capable entity that does NOT already
 * carry a per-scale `AnnotScaleContext` for the new scale should get one
 * derived from its master fields. AutoCAD performs this fan-out as a
 * SINGLE undoable transaction — we mirror that behaviour by mutating the
 * document immutably in one pass and returning the new shape, so the
 * reducer's caller records exactly one history step.
 *
 * Inverse pattern of `useCannoscaleResolver` — the resolver READS
 * contexts to render; this module WRITES contexts from master fields.
 *
 * ## Scope (PR-6 only)
 *
 *   - TEXT, MTEXT, HATCH, INSERT, ATTDEF — full materialization.
 *   - DIMENSION, MULTILEADER — SKIPPED (legacy `annotativeContexts`
 *     schemas; widening lands in PR-7). Walking past them must not
 *     throw — we just leave them untouched.
 *   - LEADER, TOLERANCE — also skipped here (not annotative-capable in
 *     our schema yet; introduced in PR-11).
 *
 * ## Idempotency
 *
 * If an entity already has a context for `scale.name` we leave it alone
 * — re-running the materializer must be a no-op for already-materialized
 * entities. The single undo-step contract relies on this; otherwise a
 * second CANNOSCALE switch back-and-forth would balloon the context list.
 *
 * ## Block-children walk
 *
 * Annotative TEXT/MTEXT/INSERT/HATCH inside a BLOCK definition (i.e. the
 * `entities` array of `document.blocks[name]`) also need contexts when
 * the host block is annotative. We integrate that walk directly into
 * `materializeScaleAcrossDocument` so the reducer only calls one helper.
 *
 * ## Performance
 *
 * АБИ1.dxf has 143 annotative entities. The walk is O(N) per pass —
 * single shallow scan of every space's entities array plus every
 * blockDef.entities array. No nested re-validation; we build the new
 * arrays imperatively and let the reducer parse the final document
 * once at the end.
 */

import type {
  AnnotScaleContext,
  AttdefEntity,
  BlockInsertEntity,
  DrawingDocument,
  HatchEntity,
  MtextEntity,
  TextEntity,
} from "@dxf-ai-editor/drawing-model";

/**
 * Annotative-capable entity types that the materializer handles. The
 * union is intentionally narrower than the full `DrawingEntity` — only
 * entity types whose `annotativeContexts` field is typed against the
 * new `AnnotScaleContext` discriminated union (PR-1) belong here.
 *
 * Notably absent: DIMENSION + MULTILEADER (they still use the legacy
 * `annotativeContextSchema` / `mleaderContextDataSchema` shapes; PR-7
 * widens them to the new union).
 */
export type AnnotativeEntity =
  | TextEntity
  | MtextEntity
  | HatchEntity
  | BlockInsertEntity
  | AttdefEntity;

/**
 * The minimum shape we need from a scale catalogue entry to derive a
 * context. Mirrors `DrawingDocument.annotationScales[]` element shape so
 * callers can pass the entry through unchanged.
 */
export type ScaleCatalogueEntry = {
  name: string;
  scaleFactor: number;
};

/** Type guard: does this entity carry the new `AnnotScaleContext` array? */
function isAnnotativeEntityType(
  entity: { type: string },
): entity is AnnotativeEntity {
  return (
    entity.type === "Text" ||
    entity.type === "MText" ||
    entity.type === "Hatch" ||
    entity.type === "Block" ||
    entity.type === "Attdef"
  );
}

/**
 * Returns true if entity has an `annotativeContexts` array with at
 * least one entry whose `scaleName` matches the given scale.
 *
 * Defensive against entities that don't carry the field (TypeScript
 * narrowing alone can't cover the case where an upstream parser dropped
 * it). Treats `undefined` as "no contexts" rather than throwing.
 */
export function entityHasContextForScale(
  entity: { annotativeContexts?: ReadonlyArray<AnnotScaleContext> },
  scaleName: string,
): boolean {
  const contexts = entity.annotativeContexts;
  if (!contexts || contexts.length === 0) return false;
  for (const ctx of contexts) {
    if (ctx.scaleName === scaleName) return true;
  }
  return false;
}

/**
 * Derive a fresh `AnnotScaleContext` for the given entity at the given
 * scale by mirroring the entity's master geometry fields. The returned
 * variant kind matches the entity's type.
 *
 * Used by:
 *   - ANNOAUTOSCALE fan-out (this module's `materializeScaleAcrossDocument`)
 *   - OBJECTSCALE add commands (PR-7+, when those grow per-entity-type
 *     materialization)
 *
 * The derived context carries `flags: 0` because the master state is
 * the implicit baseline — AutoCAD reserves non-zero flags for explicit
 * user-set overrides recorded after the initial materialization.
 */
export function deriveContextFromMaster(
  entity: AnnotativeEntity,
  scale: ScaleCatalogueEntry,
): AnnotScaleContext {
  const base = {
    scaleName: scale.name,
    scaleFactor: scale.scaleFactor,
    flags: 0,
  } as const;

  switch (entity.type) {
    case "Text":
      return {
        ...base,
        kind: "text",
        insertion: { x: entity.insertionPoint.x, y: entity.insertionPoint.y },
        // alignmentPoint is optional on the entity — only surface it when
        // present so the resolver doesn't synthesize a (0, 0) alignment.
        ...(entity.alignmentPoint
          ? {
              alignment: {
                x: entity.alignmentPoint.x,
                y: entity.alignmentPoint.y,
              },
            }
          : {}),
        rotation: entity.rotation,
      };

    case "MText":
      return {
        ...base,
        kind: "mtext",
        insertion: { x: entity.insertionPoint.x, y: entity.insertionPoint.y },
        rotation: entity.rotation,
        // referenceWidth = 0 on the entity means "unbounded" — we still
        // round-trip the value (incl. 0) so the writer can re-emit it.
        width: entity.referenceWidth,
        attachment: entity.attachmentPoint,
      };

    case "Hatch":
      return {
        ...base,
        kind: "hatch",
        patternScale: entity.patternScale,
        patternAngle: entity.patternAngle,
      };

    case "Block":
      return {
        ...base,
        kind: "blockref",
        insertion: { x: entity.insertionPoint.x, y: entity.insertionPoint.y },
        scaleX: entity.scaleX,
        scaleY: entity.scaleY,
        rotation: entity.rotation,
      };

    case "Attdef":
      return {
        ...base,
        kind: "attribute",
        insertion: { x: entity.insertionPoint.x, y: entity.insertionPoint.y },
        rotation: entity.rotation,
        // ATTDEF master tag — non-empty per the entity schema; the
        // attribute context records this so a renderer can pair the
        // context to the right ATTRIB instance later.
        attribTag: entity.tag.length > 0 ? entity.tag : "TAG",
      };
  }
}

/**
 * Append a derived context to the entity's `annotativeContexts` array
 * if it does not already have one for `scale.name`. Returns the
 * (possibly new) entity reference. When idempotent (already had it)
 * the original reference is returned unchanged so the caller can
 * detect "no work needed" via referential equality.
 */
function ensureContextOnEntity(
  entity: AnnotativeEntity,
  scale: ScaleCatalogueEntry,
): { entity: AnnotativeEntity; added: boolean } {
  if (entityHasContextForScale(entity, scale.name)) {
    return { entity, added: false };
  }
  const derived = deriveContextFromMaster(entity, scale);
  // Spread keeps the entity type intact; TypeScript can't narrow the
  // discriminated union through a generic spread, so we cast back to
  // AnnotativeEntity. The shape is preserved field-for-field; only
  // `annotativeContexts` is replaced with the appended array.
  const next = {
    ...entity,
    annotativeContexts: [...(entity.annotativeContexts ?? []), derived],
  } as AnnotativeEntity;
  return { entity: next, added: true };
}

export type MaterializationResult = {
  document: DrawingDocument;
  affectedEntityCount: number;
};

/**
 * Walk every space's entities AND every block definition's child
 * entities, adding a derived context for `scale` to every annotative
 * entity that lacks one. Returns the (possibly new) document plus a
 * count of entities affected for audit / telemetry purposes.
 *
 * Does NOT call `assembleDrawingDocument` itself — leaves that to
 * the caller (the reducer wraps the returned document in a single
 * assemble call so the whole transaction is one validation pass).
 *
 * If `affectedEntityCount === 0` the original document reference is
 * returned unchanged so callers can short-circuit with a referential
 * equality check.
 */
export function materializeScaleAcrossDocument(
  document: DrawingDocument,
  scale: ScaleCatalogueEntry,
): MaterializationResult {
  let affectedEntityCount = 0;

  // Pass 1 — every space's entities array.
  const nextSpaces = document.spaces.map((space) => {
    let spaceChanged = false;
    const nextEntities = space.entities.map((entity) => {
      if (!isAnnotativeEntityType(entity)) return entity;
      const result = ensureContextOnEntity(entity, scale);
      if (result.added) {
        affectedEntityCount += 1;
        spaceChanged = true;
        // Cast back to DrawingEntity union — ensureContextOnEntity
        // preserved the entity's discriminator field so the cast is
        // structurally safe.
        return result.entity as (typeof space.entities)[number];
      }
      return entity;
    });
    if (!spaceChanged) return space;
    return { ...space, entities: nextEntities };
  });

  // Pass 2 — every block definition's children. AutoCAD's annotative
  // INSERT can reference a block whose body itself contains annotative
  // TEXT/MTEXT/INSERT/HATCH; those need contexts too so the renderer
  // can pick the right per-scale fields when drawing the block instance.
  let blocksChanged = false;
  const nextBlocks: typeof document.blocks = {};
  for (const [name, blockDef] of Object.entries(document.blocks)) {
    let blockChanged = false;
    const nextChildren = blockDef.entities.map((child) => {
      if (!isAnnotativeEntityType(child)) return child;
      const result = ensureContextOnEntity(child, scale);
      if (result.added) {
        affectedEntityCount += 1;
        blockChanged = true;
        return result.entity as (typeof blockDef.entities)[number];
      }
      return child;
    });
    if (blockChanged) {
      nextBlocks[name] = { ...blockDef, entities: nextChildren };
      blocksChanged = true;
    } else {
      nextBlocks[name] = blockDef;
    }
  }

  if (affectedEntityCount === 0) {
    return { document, affectedEntityCount: 0 };
  }

  const nextDocument: DrawingDocument = {
    ...document,
    spaces: nextSpaces,
    ...(blocksChanged ? { blocks: nextBlocks } : {}),
  };

  return { document: nextDocument, affectedEntityCount };
}
